import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, CircularProgress, Stack } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { LineChart } from '@mui/x-charts/LineChart';
import api from '../../services/api';
import DeltaChip from './DeltaChip';

const RANGE_LABEL = { '24h': 'últimas 24h', '7d': 'últimos 7 dias', '30d': 'últimos 30 dias' };

/**
 * Timeline dual: autenticações OK vs Falhas.
 * Faz 2 requests paralelos pra /auth_ok_timeline e /auth_fail_timeline.
 */
export default function AuthTimelineCard({ range = '24h' }) {
    const [ok, setOk] = useState(null);
    const [fail, setFail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/dashboard/driade/auth_ok_timeline?range=${range}`).then((r) => r.data).catch(() => null),
            api.get(`/dashboard/driade/auth_fail_timeline?range=${range}`).then((r) => r.data).catch(() => null),
        ]).then(([okData, failData]) => {
            setOk(okData);
            setFail(failData);
            setLoading(false);
        });
    }, [range]);

    const okData = (ok?.buckets || []).map((b) => b.count);
    const failData = (fail?.buckets || []).map((b) => b.count);
    const labels = (ok?.buckets || fail?.buckets || []).map((b) => formatLabel(b.hour, range));

    const okTotal = ok?.total || 0;
    const failTotal = fail?.total || 0;
    const total = okTotal + failTotal;
    const failRate = total > 0 ? ((failTotal / total) * 100).toFixed(1) : '0.0';

    return (
        <Paper
            elevation={2}
            sx={{
                m: 2,
                p: 2,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid #e01b29',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VpnKeyIcon color="primary" />
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Autenticações — {RANGE_LABEL[range]}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                        ✓ {okTotal.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                        ✗ {failTotal.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({failRate}% falha)
                    </Typography>
                </Stack>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <DeltaChip value={ok?.delta_pct || 0} label="OK vs anterior" />
                <DeltaChip value={fail?.delta_pct || 0} label="falhas vs anterior" inverted />
            </Stack>

            {loading ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : total === 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                    <Typography variant="body2" color="text.secondary">
                        Sem autenticações registradas no período
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, minHeight: 180 }}>
                    <LineChart
                        height={180}
                        series={[
                            { data: okData, label: 'OK', color: '#2e7d32', curve: 'monotoneX', showMark: range === '24h' },
                            { data: failData, label: 'Falha', color: '#e01b29', curve: 'monotoneX', showMark: range === '24h' },
                        ]}
                        xAxis={[{ data: labels, scaleType: 'point' }]}
                        margin={{ left: 40, right: 16, top: 16, bottom: 32 }}
                        sx={{
                            '& .MuiLineElement-root': { strokeWidth: 2 },
                        }}
                        slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'right' } } }}
                    />
                </Box>
            )}
        </Paper>
    );
}

function formatLabel(iso, range) {
    const d = new Date(iso);
    if (range === '24h') return `${String(d.getHours()).padStart(2, '0')}h`;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
